import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatsCards } from './components/StatsCards';
import { RevenueChart } from './components/RevenueChart';
import { UpcomingBills } from './components/UpcomingBills';
import { RecentTransactions } from './components/RecentTransactions';
import { InsightsWidget } from './components/InsightsWidget';
import { UserProgressWidget } from './components/UserProgressWidget';
import { SpendingAlertBanner, SpendingAlertPrompt } from './components/SpendingAlertBanner';
import { SpendingAlertConfigModal } from './components/SpendingAlertConfigModal';
import { ProfileCompletenessCard, calculateProfileCompleteness } from '../perfil/components/ProfileCompletenessCard';
import { SetupChecklist } from '../../components/feature/SetupChecklist';
import { useAuth } from '../../contexts/AuthContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import { useTransactions } from '../../hooks/useTransactions';
import { useSpendingAlerts } from '../../hooks/useSpendingAlerts';
import { useSmartNotifications } from '../../hooks/useSmartNotifications';
import type { TransactionPayload } from '../../hooks/useTransactions';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { TransactionModal } from '../lancamentos/components/TransactionModal';
import { UpgradeModal } from '../../components/feature/UpgradeModal';
import { DowngradeLimitsBanner } from '../../components/feature/DowngradeLimitsBanner';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
};

const today = new Date().toLocaleDateString('pt-BR', {
  weekday: 'long',
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

interface ToastState { message: string; type: 'success' | 'error' }

export default function DashboardPage() {
  const { profile } = useAuth();
  const { loading, stats, accounts, chartRawData, upcomingBills, recentTransactions, refetch } =
    useDashboardData();
  const navigate = useNavigate();

  // ─── Smart notifications (runs once per session) ─────────────────────────
  useSmartNotifications();

  // ─── Real transaction hooks ──────────────────────────────────────────────────
  const now = new Date();
  const {
    createTransaction,
    createManyTransactions,
    transactions,
  } = useTransactions({ month: now.getMonth() + 1, year: now.getFullYear() });

  const { checkTransactionLimit, monthlyTransactionLimit } = usePlanLimits();

  // ─── Spending alerts ─────────────────────────────────────────────────────────
  const spendingAlert = useSpendingAlerts(stats.monthlyExpenses, stats.monthlyIncome);
  const [showAlertConfig, setShowAlertConfig] = useState(false);
  const [alertDismissed, setAlertDismissed] = useState(false);

  useEffect(() => {
    setAlertDismissed(false);
  }, [spendingAlert.level]);

  // ─── UI state ────────────────────────────────────────────────────────────────
  const [showModal,    setShowModal]    = useState(false);
  const [showUpgrade,  setShowUpgrade]  = useState(false);
  const [toast,        setToast]        = useState<ToastState | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const handleNewTransaction = async () => {
    const check = await checkTransactionLimit();
    if (!check.allowed) { setShowUpgrade(true); return; }
    setShowModal(true);
  };

  const handleSave = async (
    values: TransactionPayload
  ): Promise<{ success: boolean; message: string }> => {
    let result: { success: boolean; message: string };

    if (!values.installments || values.installments <= 1) {
      result = await createTransaction(values);
    } else {
      const total = Number(values.amount);
      const parcelaBase = parseFloat((total / values.installments).toFixed(2));
      const groupId = crypto.randomUUID();
      const baseDate = new Date(values.date + 'T00:00:00');

      let sumSoFar = 0;
      const records: TransactionPayload[] = Array.from({ length: values.installments }, (_, i) => {
        const isLast = i === values.installments - 1;
        const amount = isLast
          ? parseFloat((total - sumSoFar).toFixed(2))
          : parcelaBase;
        if (!isLast) sumSoFar += parcelaBase;
        const d = new Date(baseDate);
        d.setMonth(baseDate.getMonth() + i);
        return {
          ...values,
          amount,
          installment_number: i + 1,
          installments: values.installments,
          installment_group_id: groupId,
          date: d.toISOString().split('T')[0],
          description: `${values.description} (${i + 1}/${values.installments})`,
          status: i === 0 ? values.status : 'pendente',
        };
      });

      result = await createManyTransactions(records);
    }

    if (result.success) {
      showToast(result.message, 'success');
      if (typeof refetch === 'function') refetch();
    }
    return result;
  };

  const displayName =
    profile?.name ||
    profile?.full_name?.split(' ')[0] ||
    'você';

  const completeness = profile ? calculateProfileCompleteness(profile) : null;

  // Detect user state for SetupChecklist
  const hasAccounts = accounts.length > 0;
  const hasTransactions = transactions.length > 0;
  const hasGoals = stats.activeGoalsCount > 0;

  return (
    <div className="space-y-6 w-full">
      {/* Header greeting */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-700 text-gray-900">
            {getGreeting()}, {displayName}! 👋
          </h2>
          <p className="text-sm text-gray-500 mt-0.5 capitalize">{today}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleNewTransaction}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-add-line text-base" />
            Novo Lançamento
          </button>
        </div>
      </div>

      {/* Downgrade limits banner */}
      <DowngradeLimitsBanner />

      {/* ─── Setup Checklist (new users) ──────────────────────────────────── */}
      {!loading && (
        <SetupChecklist
          hasAccounts={hasAccounts}
          hasTransactions={hasTransactions}
          hasGoals={hasGoals}
        />
      )}

      {/* ─── Spending Alert ───────────────────────────────────────────────── */}
      {!spendingAlert.loading && (
        spendingAlert.config.enabled
          ? (!alertDismissed && (spendingAlert.level === 'warning' || spendingAlert.level === 'danger') && (
              <SpendingAlertBanner
                level={spendingAlert.level}
                pctUsed={spendingAlert.pctUsed}
                monthlyExpenses={spendingAlert.monthlyExpenses}
                referenceIncome={spendingAlert.referenceIncome}
                usingCustomIncome={spendingAlert.usingCustomIncome}
                config={spendingAlert.config}
                onConfigure={() => setShowAlertConfig(true)}
                onDismiss={() => setAlertDismissed(true)}
              />
            ))
          : (
            <SpendingAlertPrompt onActivate={() => setShowAlertConfig(true)} />
          )
      )}

      {/* Profile completeness banner */}
      {profile && completeness && completeness.pct < 80 && (
        <ProfileCompletenessCard
          profile={profile}
          onComplete={() => navigate('/perfil')}
        />
      )}

      {/* Quick accounts summary */}
      <div className="flex items-center gap-3 flex-wrap">
        {loading ? (
          <div className="flex gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 w-36 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Nenhuma conta cadastrada ainda.</p>
        ) : (
          accounts.map((acc) => (
            <div
              key={acc.id}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-100 rounded-xl"
            >
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: acc.color }} />
              <span className="text-xs font-500 text-gray-600">{acc.name}</span>
              <span className="text-xs font-700 text-gray-900">
                {acc.balance.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </span>
            </div>
          ))
        )}
        <button
          onClick={() => navigate('/contas')}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-emerald-600 font-600 hover:bg-emerald-50 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-add-circle-line text-sm" />
          Adicionar conta
        </button>
      </div>

      {/* Stats cards */}
      <StatsCards stats={stats} loading={loading} />

      {/* Chart + Upcoming bills */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-3">
          <RevenueChart chartRawData={chartRawData} loading={loading} />
        </div>
        <div className="xl:col-span-2">
          <UpcomingBills bills={upcomingBills} loading={loading} />
        </div>
      </div>

      {/* Recent transactions */}
      <RecentTransactions transactions={recentTransactions} loading={loading} />

      {/* Insights + Progress side by side */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <InsightsWidget stats={stats} loading={loading} />
        <UserProgressWidget stats={stats} accounts={accounts} loading={loading} />
      </div>

      {/* ─── Real TransactionModal ──────────────── */}
      <TransactionModal
        key="dashboard-new"
        open={showModal}
        transaction={null}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
      />

      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="Limite de lançamentos atingido"
        description={`O plano Free permite apenas ${monthlyTransactionLimit} lançamentos por mês. Faça upgrade para registrar sem limites.`}
      />

      {/* Spending Alert Config Modal */}
      <SpendingAlertConfigModal
        open={showAlertConfig}
        config={spendingAlert.config}
        saving={spendingAlert.saving}
        onClose={() => setShowAlertConfig(false)}
        onSave={spendingAlert.saveConfig}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl font-500 text-sm ${
            toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
          }`}
        >
          <i
            className={
              toast.type === 'success'
                ? 'ri-check-circle-line text-base'
                : 'ri-error-warning-line text-base'
            }
          />
          {toast.message}
        </div>
      )}
    </div>
  );
}
